import type { SearchPanelResult } from "../../molecules/search-panel/search-panel";
import { isSafeHref } from "../../services/safe-href";
import type {
  AzureSuggestResponse,
  ProductSuggestRow,
  SupportSuggestRow
} from "./search-suggest.types";

/** AC-8's two categories, and the two suggesters behind them. */
export const PRODUCT_GROUP = "Product and Service";
export const SUPPORT_GROUP = "Support";

/* Support paths, transcribed from the published search component rather than
   guessed. Anything unrecognised falls through to the common article form,
   which is what prod does too. */
const SUPPORT_PATHS = {
  common: "/support/article/",
  commonWorkflow: "/support/",
  internet: "/support/internet/article/",
  satelliteArticle: "/support/satellite/article/",
  satelliteVideo: "/support/satellite/?video=",
  satelliteWorkflow: "/support/satellite/",
  streamArticle: "/support/stream/article/",
  streamVideo: "/support/stream/?video=",
  streamWorkflow: "/support/stream/"
} as const;

/** No plaintext links out of a first-party suggestion list. */
const HREF_SCHEMES = ["https:"] as const;

const text = (value?: string): string => (typeof value === "string" ? value.trim() : "");

/**
 * The support link, rebuilt from the row — `publishedId` is an article id, not
 * a path. Mirrors the production component's branching exactly, including the
 * `?`-split on workflow rows, whose ids arrive as `something?foo=bar` and
 * contribute only the query half.
 *
 * An id that is already a URL is handed back untouched, which is the one case
 * prod skips the whole table for.
 */
export function supportHref(row: SupportSuggestRow): string {
  const id = text(row?.publishedId);
  if (id === "") {
    return "";
  }
  const category = text(row?.contentCategory);
  const service = text(row?.service);
  if (id.startsWith("http") && category !== "workflow") {
    return id;
  }
  /* A workflow id carries its own query string; only the half after `?`
     survives, so a missing `?` yields "" and the row is dropped downstream
     rather than linking at the bare support root. */
  const workflowQuery = (): string => {
    const query = id.split("?")[1];
    return query === undefined || query === "" ? "" : query;
  };

  if (service === "internet") {
    return `${SUPPORT_PATHS.internet}${id}`;
  }
  if (service === "satellite") {
    if (category === "video") {
      return `${SUPPORT_PATHS.satelliteVideo}${id}`;
    }
    if (category === "workflow") {
      const query = workflowQuery();
      return query === "" ? "" : `${SUPPORT_PATHS.satelliteWorkflow}?${query}`;
    }
    return `${SUPPORT_PATHS.satelliteArticle}${id}`;
  }
  if (service === "stream") {
    if (category === "video") {
      return `${SUPPORT_PATHS.streamVideo}${id}`;
    }
    if (category === "workflow") {
      const query = workflowQuery();
      return query === "" ? "" : `${SUPPORT_PATHS.streamWorkflow}?${query}`;
    }
    return `${SUPPORT_PATHS.streamArticle}${id}`;
  }
  if (service === "common" && category === "workflow") {
    const query = workflowQuery();
    return query === "" ? "" : `${SUPPORT_PATHS.commonWorkflow}?${query}`;
  }
  return `${SUPPORT_PATHS.common}${id}`;
}

/**
 * Rows to suggestions, shared by both suggesters.
 *
 * ⚠ THE ORDER OF THE THREE STEPS IS THE POINT. Drop anything unusable first,
 * deduplicate by title second (the suggesters return the same article under
 * several ids), cap third — so an unsafe duplicate can never claim a title
 * ahead of a usable one, and neither can consume one of the slots AC-9
 * promises to a row the reader will actually see.
 *
 * `isSafeHref` is the same rule the panel, the nav and the footer use,
 * narrowed to https: a support path is relative and resolves against the
 * directv origin, and a product url arrives absolute on it. A `javascript:` or
 * other scheme, an `http:` downgrade, or userinfo smuggling fails here rather
 * than at render, where it would already have cost a slot. ⚠ It is not a host
 * allowlist: an explicit `https://` to another host still passes.
 */
function toResults<TRow>(
  rows: TRow[] | undefined,
  cap: number,
  group: string,
  label: (row: TRow) => string,
  href: (row: TRow) => string
): SearchPanelResult[] {
  if (!Array.isArray(rows)) {
    return [];
  }
  const seen = new Set<string>();
  const results: SearchPanelResult[] = [];
  for (const row of rows) {
    if (results.length >= cap) {
      break;
    }
    const rowLabel = label(row);
    const rowHref = href(row);
    if (rowLabel === "" || rowLabel === "undefined" || !isSafeHref(rowHref, HREF_SCHEMES)) {
      continue;
    }
    const key = rowLabel.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    results.push({
      label: rowLabel,
      href: rowHref,
      group
    });
  }
  return results;
}

/** `idx-prd-product` rows. `url` is absolute already. */
export function toProductResults(raw: unknown, cap: number): SearchPanelResult[] {
  const body = raw as AzureSuggestResponse<ProductSuggestRow> | undefined;
  return toResults<ProductSuggestRow>(
    body?.value,
    cap,
    PRODUCT_GROUP,
    row => text(row?.title),
    row => text(row?.url)
  );
}

/** `idx-prd-supp` rows. The link is rebuilt — see `supportHref`. */
export function toSupportResults(raw: unknown, cap: number): SearchPanelResult[] {
  const body = raw as AzureSuggestResponse<SupportSuggestRow> | undefined;
  return toResults<SupportSuggestRow>(
    body?.value,
    cap,
    SUPPORT_GROUP,
    row => text(row?.title),
    row => supportHref(row)
  );
}
